import { AlertTriangle } from "lucide-react";
import type { ReactNode } from "react";
import { NoticeBox } from "@/components/NoticeBox";

interface MediaPermissionAlertProps {
  message: string;
  /** Optional extra content (e.g. a link to an alternative, upload-based
   * tool) rendered below the message -- for cases where there's a real,
   * genuine fallback the person can use instead of just being stuck. */
  children?: ReactNode;
}

/**
 * A persistent, inline error banner for camera/microphone access
 * problems, used in place of a toast for this specific case. A toast
 * is a floating, transient, auto-dismissing element -- on a real
 * mobile screen it can end up rendered on top of or overlapping other
 * page content, making a message that's genuinely important to read
 * carefully (specific recovery steps) hard or impossible to actually
 * read, exactly what happened in practice. Rendering inline, as a
 * normal part of the page's own layout, means it pushes surrounding
 * content down rather than floating over it, and stays visible until
 * the person dismisses it or fixes the underlying problem and retries.
 */
export function MediaPermissionAlert({ message, children }: MediaPermissionAlertProps) {
  return (
    <NoticeBox className="flex gap-2 items-start text-sm" role="alert">
      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
      <div>
        <p>{message}</p>
        {children}
      </div>
    </NoticeBox>
  );
}
